import { inject, injectable } from 'inversify';
import { TYPES } from '@Types';
import {
  army,
  army_full,
  army_last_update,
  army_small,
  army_sources,
} from '@Api';
import { Army, ArmySource, ArmyVersioning } from '@Models';
import { ArmyOrganisationMapper } from './army-organisation.mapper';
import { ModelRuleMapper } from './model-rule.mapper';
import { MagicItemCategoryMapper } from './MagicItemCategoryMapper';
import { MagicItemMapper } from './magic-item.mapper';
import { MagicStandardMapper } from './magic-standard.mapper';
import { OrganisationMapper } from './OrganisationMapper';
import { UnitMapper } from './UnitMapper';
import { ModelRuleLimitMapper } from './ModelRuleLimitMapper';
import { IMapper } from '../IMapper';
import { MagicSpellMapper } from '@Mapper/ninthAge/MagicSpellMapper';

@injectable()
export class ArmyMapper implements IMapper<number, army_full, army, Army> {
  @inject(TYPES.UnitMapper) private _unitMapper!: UnitMapper;
  @inject(TYPES.Mapper.ModelRuleMapper)
  private _modelRuleMapper!: ModelRuleMapper;
  @inject(TYPES.MagicItemCategoryMapper)
  private _magicItemCategoryMapper!: MagicItemCategoryMapper;
  @inject(TYPES.MagicItemMapper) private _magicItemMapper!: MagicItemMapper;
  @inject(TYPES.MagicStandardMapper)
  private _magicStandardMapper!: MagicStandardMapper;
  @inject(TYPES.OrganisationMapper)
  private _organisationMapper!: OrganisationMapper;
  @inject(TYPES.ArmyOrganisationMapper)
  private _armyOrganisationMapper!: ArmyOrganisationMapper;
  @inject(TYPES.Mapper.ModelRuleLimitMapper)
  private _modelRuleLimitMapper!: ModelRuleLimitMapper;
  @inject(TYPES.Mapper.MagicSpellMapper)
  private _magicSpellMapper!: MagicSpellMapper;

  public mapFull(item: army): Army {
    const organisations =
      item.organisations
        ?.map((item) => this._organisationMapper.map(item))
        ?.sort((a, b) => a.position - b.position) ?? [];

    return {
      id: item.id,
      versionId: item.version_id,
      categoryId: item.category_id,
      updatedAt: item.updated_at,
      name: item.name,
      nameWithSubVersion: item.name_with_sub_version,
      initials: item.initials,
      medium_logo: item.medium_logo,
      large_logo: item.large_logo,
      source: this.mapSource(item.source),
      units: item.units?.map((item) => this._unitMapper.map(item)) ?? [],
      modelRules:
        item.model_rules?.map((item) => this._modelRuleMapper.map(item)) ?? [],
      modelRuleLimits:
        item.model_rule_limits?.map((item) =>
          this._modelRuleLimitMapper.map(item),
        ) ?? [],
      magic_item_categories:
        item.magic_item_categories?.map((item) =>
          this._magicItemCategoryMapper.map(item),
        ) ?? [],
      magic_items:
        item.magic_items?.map((item) => this._magicItemMapper.map(item)) ?? [],
      magic_standards:
        item.magic_standards?.map((item) =>
          this._magicStandardMapper.map(item),
        ) ?? [],
      organisations: organisations,
      army_organisations:
        item.army_organisations?.map((item) =>
          this._armyOrganisationMapper.map(item),
        ) ?? [],
      spells:
        item.spells?.map((spell) => this._magicSpellMapper.map(spell)) ?? [],
      isLatest: item.is_latest,
    } satisfies Army as Army;
  }

  public map(item: army_full): Army {
    return {
      id: item.id,
      categoryId: item.category_id,
      versionId: item.version_id,
      updatedAt: item.updated_at,
      name: item.name,
      nameWithSubVersion: item.name_with_sub_version,
      initials: '',
      medium_logo: item.medium_logo,
      large_logo: item.large_logo,
      source: this.mapSource(item.source),
      units: [],
      modelRules: [],
      magic_item_categories: [],
      magic_items: [],
      magic_standards: [],
      organisations: [],
      army_organisations: [],
      modelRuleLimits: [],
      isLatest: item.is_latest,
      spells: [],
    } satisfies Army as Army;
  }

  public mapSmall(item: army_small): Army {
    return {
      id: item.id,
      categoryId: 0,
      versionId: 0,
      updatedAt: 0,
      name: item.name,
      nameWithSubVersion: item.name,
      initials: item.initials,
      source: ArmySource.Official,
      medium_logo: null,
      large_logo: null,
      units: [],
      modelRules: [],
      magic_item_categories: [],
      magic_items: [],
      magic_standards: [],
      organisations: [],
      army_organisations: [],
      modelRuleLimits: [],
      isLatest: false,
      spells: [],
    } satisfies Army as Army;
  }

  public mapVersioning(data: army_last_update): ArmyVersioning {
    return {
      id: data.id,
      name: data.name ?? '',
      updatedAt: data.updated_at,
      versionId: data.version_id,
    } satisfies ArmyVersioning as ArmyVersioning;
  }

  public mapSource(item: army_sources): ArmySource {
    switch (item) {
      case army_sources.SUPPLEMENT:
        return ArmySource.Supplement;
      case army_sources.FAN_MADE:
        return ArmySource.FanMade;
    }

    return ArmySource.Official;
  }
}
